/**
 * Geometry helpers for drawing and importing contours.
 *
 * The area shown while drawing is a spherical estimate: it exists so the operator sees
 * the size before paying for a GEE run. The authoritative area is PostGIS
 * `ST_Area(geom::geography)` computed on insert (db/repo.py), and it is that number the
 * UI shows once the field is saved — the two differ by well under a percent, and we
 * never quote the estimate next to a saved field.
 */

import type { GeoJsonPolygon } from "../api/types";
import { T } from "../strings";

const EARTH_RADIUS_M = 6_378_137;

type Ring = number[][];

const RAD = Math.PI / 180;

/**
 * Spherical ring area: sum over vertices of (λ_{i+1} − λ_{i−1})·sin(φ_i), times R²/2.
 * The sin(φ) term is what makes a 0.01° box at 69° N half the size of the same box at
 * 45° N — with the region running to 70° N, a planar approximation is not an option
 * (CLAUDE.md: "любое допущение о размере пикселя ломается").
 */
const ringArea = (ring: Ring): number => {
  if (ring.length < 3) return 0;
  const count = ring.length;
  let total = 0;
  for (let i = 0; i < count; i += 1) {
    const previous = ring[(i - 1 + count) % count]!;
    const current = ring[i]!;
    const next = ring[(i + 1) % count]!;
    const lonPrevious = previous[0] ?? 0;
    const lonNext = next[0] ?? 0;
    const lat = current[1] ?? 0;
    total += (lonNext - lonPrevious) * RAD * Math.sin(lat * RAD);
  }
  return (total * EARTH_RADIUS_M * EARTH_RADIUS_M) / 2;
};

const polygonArea = (rings: Ring[]): number => {
  if (rings.length === 0) return 0;
  const [outer, ...holes] = rings;
  const outerArea = Math.abs(ringArea(outer ?? []));
  const holeArea = holes.reduce((sum, hole) => sum + Math.abs(ringArea(hole)), 0);
  return Math.max(outerArea - holeArea, 0);
};

/** Spherical area in square metres of a Polygon or MultiPolygon. */
export const areaSquareMeters = (geometry: GeoJsonPolygon | null | undefined): number => {
  if (!geometry) return 0;
  if (geometry.type === "Polygon") return polygonArea(geometry.coordinates as Ring[]);
  return (geometry.coordinates as Ring[][]).reduce((sum, rings) => sum + polygonArea(rings), 0);
};

export const areaHectares = (geometry: GeoJsonPolygon | null | undefined): number =>
  areaSquareMeters(geometry) / 10_000;

export type Bbox = [number, number, number, number];

export const bboxOf = (geometry: GeoJsonPolygon | null | undefined): Bbox | null => {
  if (!geometry) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  const visit = (rings: Ring[]): void => {
    for (const ring of rings) {
      for (const point of ring) {
        const [x, y] = point;
        if (typeof x !== "number" || typeof y !== "number") continue;
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
      }
    }
  };
  if (geometry.type === "Polygon") visit(geometry.coordinates as Ring[]);
  else for (const rings of geometry.coordinates as Ring[][]) visit(rings);
  if (!Number.isFinite(minX) || !Number.isFinite(minY)) return null;
  return [minX, minY, maxX, maxY];
};

export const bboxUnion = (boxes: (Bbox | null)[]): Bbox | null => {
  const present = boxes.filter((box): box is Bbox => box !== null);
  if (present.length === 0) return null;
  return present.reduce<Bbox>(
    (acc, box) => [
      Math.min(acc[0], box[0]),
      Math.min(acc[1], box[1]),
      Math.max(acc[2], box[2]),
      Math.max(acc[3], box[3]),
    ],
    [Infinity, Infinity, -Infinity, -Infinity],
  );
};

/** Merge several polygons into one MultiPolygon (one drawn field may have parts). */
export const combinePolygons = (geometries: GeoJsonPolygon[]): GeoJsonPolygon | null => {
  const parts: Ring[][] = [];
  for (const geometry of geometries) {
    if (geometry.type === "Polygon") parts.push(geometry.coordinates as Ring[]);
    else parts.push(...(geometry.coordinates as Ring[][]));
  }
  if (parts.length === 0) return null;
  if (parts.length === 1) return { type: "Polygon", coordinates: parts[0]! };
  return { type: "MultiPolygon", coordinates: parts };
};

export interface ImportedFeature {
  name: string | null;
  geometry: GeoJsonPolygon;
}

export interface ImportResult {
  features: ImportedFeature[];
  /** Everything skipped, with a reason: no silent drops (CLAUDE.md, no `except: pass`). */
  skipped: { name: string | null; reason: string }[];
}

const isPolygonGeometry = (value: unknown): value is GeoJsonPolygon => {
  if (!value || typeof value !== "object") return false;
  const type = (value as { type?: unknown }).type;
  const coordinates = (value as { coordinates?: unknown }).coordinates;
  return (type === "Polygon" || type === "MultiPolygon") && Array.isArray(coordinates);
};

const nameOf = (properties: unknown): string | null => {
  if (!properties || typeof properties !== "object") return null;
  const record = properties as Record<string, unknown>;
  for (const key of ["name", "Name", "NAME", "title", "field", "field_name", "label"]) {
    const value = record[key];
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return null;
};

/** GeoJSON: FeatureCollection, Feature, GeometryCollection or a bare geometry. */
export const parseGeoJson = (text: string): ImportResult => {
  const result: ImportResult = { features: [], skipped: [] };
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (error) {
    result.skipped.push({
      name: null,
      reason: T.import.badJson(error instanceof Error ? error.message : T.common.unknownError),
    });
    return result;
  }

  const pushGeometry = (geometry: unknown, name: string | null): void => {
    if (isPolygonGeometry(geometry)) {
      result.features.push({ name, geometry });
      return;
    }
    const type =
      geometry && typeof geometry === "object"
        ? String((geometry as { type?: unknown }).type ?? "?")
        : "?";
    result.skipped.push({ name, reason: T.import.notAPolygon(type) });
  };

  const visit = (node: unknown): void => {
    if (!node || typeof node !== "object") return;
    const type = (node as { type?: unknown }).type;
    if (type === "FeatureCollection") {
      const features = (node as { features?: unknown }).features;
      if (Array.isArray(features)) features.forEach(visit);
      return;
    }
    if (type === "Feature") {
      const feature = node as { geometry?: unknown; properties?: unknown };
      const name = nameOf(feature.properties);
      if (
        feature.geometry &&
        (feature.geometry as { type?: unknown }).type === "GeometryCollection"
      ) {
        const geometries = (feature.geometry as { geometries?: unknown }).geometries;
        if (Array.isArray(geometries)) {
          geometries.forEach((geometry) => pushGeometry(geometry, name));
          return;
        }
      }
      pushGeometry(feature.geometry, name);
      return;
    }
    if (type === "GeometryCollection") {
      const geometries = (node as { geometries?: unknown }).geometries;
      if (Array.isArray(geometries)) geometries.forEach((geometry) => pushGeometry(geometry, null));
      return;
    }
    pushGeometry(node, null);
  };

  visit(parsed);
  return result;
};

const parseKmlCoordinates = (text: string): Ring => {
  const ring: Ring = [];
  for (const chunk of text.trim().split(/\s+/)) {
    const [lon, lat] = chunk.split(",");
    const x = Number(lon);
    const y = Number(lat);
    if (Number.isFinite(x) && Number.isFinite(y)) ring.push([x, y]);
  }
  return ring;
};

const kmlPolygon = (element: Element): GeoJsonPolygon | null => {
  const outerNode = element.querySelector("outerBoundaryIs coordinates");
  if (!outerNode?.textContent) return null;
  const outer = parseKmlCoordinates(outerNode.textContent);
  if (outer.length < 4) return null;
  const holes: Ring[] = [];
  element.querySelectorAll("innerBoundaryIs coordinates").forEach((node) => {
    const hole = parseKmlCoordinates(node.textContent ?? "");
    if (hole.length >= 4) holes.push(hole);
  });
  return { type: "Polygon", coordinates: [outer, ...holes] };
};

/**
 * KML: Placemark polygons only, which is what farm exports contain. Anything else is
 * reported as skipped rather than dropped. Altitude is discarded (the backend forces
 * 2D anyway via ST_Force2D).
 */
export const parseKml = (text: string): ImportResult => {
  const result: ImportResult = { features: [], skipped: [] };
  const document = new DOMParser().parseFromString(text, "application/xml");
  if (document.querySelector("parsererror")) {
    result.skipped.push({ name: null, reason: T.import.badKml });
    return result;
  }
  const placemarks = Array.from(document.querySelectorAll("Placemark"));
  if (placemarks.length === 0) {
    result.skipped.push({ name: null, reason: T.import.noPlacemarks });
    return result;
  }
  for (const placemark of placemarks) {
    const name = placemark.querySelector("name")?.textContent?.trim() || null;
    const polygons = Array.from(placemark.querySelectorAll("Polygon"))
      .map(kmlPolygon)
      .filter((geometry): geometry is GeoJsonPolygon => geometry !== null);
    if (polygons.length === 0) {
      result.skipped.push({ name, reason: T.import.noPolygonInPlacemark });
      continue;
    }
    const combined = combinePolygons(polygons);
    if (combined) result.features.push({ name, geometry: combined });
  }
  return result;
};

export const parseContourFile = (fileName: string, text: string): ImportResult => {
  const lower = fileName.toLowerCase();
  if (lower.endsWith(".kml") || text.trimStart().startsWith("<")) return parseKml(text);
  return parseGeoJson(text);
};
